/**
 * The collapseWhitespace function is adapted from collapse-whitespace
 * by Luc Thevenard.
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2014 Luc Thevenard <lucthevenard@gmail.com>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */

import { NodeTypes } from "./node";
import { isWhitespacePreserved } from "./utilities";

/**
 * collapseWhitespace(options) removes extraneous whitespace from an the given element.
 *
 * @param {Object} options
 */
interface CollapseWhitespaceOptions {
  element: Node;
  isBlock: (node: Node) => boolean;
  isVoid: (node: Node) => boolean;
  isPre?: (node: Node) => boolean;
}

function collapseWhitespace(options: CollapseWhitespaceOptions): void {
  const element = options.element
  const isBlock = options.isBlock
  const isVoid = options.isVoid
  const isPre = options.isPre || isWhitespacePreserved;

  if (!element.firstChild || isPre(element)) return

  let prevText: Text | null = null;
  let keepLeadingWs = false

  let prev: Node | null = null;
  let node: Node = next(prev, element, isPre);

  while (node !== element) {
    if (node.nodeType === NodeTypes.Text || node.nodeType === NodeTypes.CDATASection) {
      const textNode = node as Text;
      let text = textNode.data.replace(/[ \r\n\t]+/g, ' ');

      if ((!prevText || / $/.test(prevText.data)) &&
        !keepLeadingWs && text[0] === ' ') {
        text = text.substr(1);
      }

      // `text` might be empty at this point.
      if (!text) {
        node = remove(node);
        continue;
      }

      textNode.data = text;

      prevText = textNode;
    } else if (node.nodeType === NodeTypes.Element) {
      if (isBlock(node) || node.nodeName === 'BR') {
        if (prevText) {
          prevText.data = prevText.data.replace(/ $/, '');
        }

        prevText = null;
        keepLeadingWs = false;
      } else if (isVoid(node) || isPre(node)) {
        // Avoid trimming space around non-block, non-BR void elements and inline PRE.
        prevText = null;
        keepLeadingWs = true;
      } else if (prevText) {
        // Drop protection if set previously.
        keepLeadingWs = false;
      }
    } else {
      node = remove(node);
      continue;
    }

    const nextNode = next(prev, node, isPre);
    prev = node;
    node = nextNode;
  }

  if (prevText) {
    prevText.data = prevText.data.replace(/ $/, '');
    if (!prevText.data) {
      remove(prevText);
    }
  }
}

/**
 * remove(node) removes the given node from the DOM and returns the
 * next node in the sequence.
 *
 * @param {Node} node
 * @return {Node} node
 */
function remove(node: Node): Node {
  const nextNode: Node | null = node.nextSibling ?? node.parentNode;
  if (node.parentNode) {
    node.parentNode.removeChild(node);
  }
  return nextNode as Node;
}

/**
 * next(prev, current, isPre) returns the next node in the sequence, given the
 * current and previous nodes.
 *
 * @param {Node} prev
 * @param {Node} current
 * @param {Function} isPre
 * @return {Node}
 */
function next(prev: Node | null, current: Node, isPre: (node: Node) => boolean): Node {
  if ((prev && prev.parentNode === current) || isPre(current)) {
    const nextNode: Node | null = current.nextSibling ?? current.parentNode;
    return nextNode as Node;
  }
  const nextNode: Node | null = current.firstChild ?? current.nextSibling ?? current.parentNode;
  return nextNode as Node;
}

export default collapseWhitespace
